// ============================================================
// ManualInviteBanner.js
// Shown on the Client detail page when client.manualInviteRequired is
// set — i.e. the trial-approval auto-invite failed at the
// issueInvitation step (Tenant/Client/provision all succeeded). Surfaces
// the failure reason and a "Send invitation" action.
//
// The send dialog issues a fresh invitation via the existing
// issueInvitation endpoint; on success it clears the flag
// (clearClientManualInviteRequired) and refreshes the header.
//
// Slice A (2026-07-11) upgrades the first-cut UX:
//   - PREFILL: on dialog open, the linked trial requester is resolved
//     via the new ?clientId= reverse lookup on admin-trials-list and
//     the name/email fields pre-populate (best-effort — blank on any
//     failure, and never clobbers text the operator already typed).
//   - RESEND: the issue endpoint's 409 duplicate-pending guard now
//     offers a one-click "Resend the existing invitation" (the new
//     mgmt/invitations/{id}/resend endpoint — token rotates, expiry
//     bumps, branded email re-sends) instead of the revoke-first
//     dead end. The pending row is resolved via listInvitations
//     (ApiError does not carry the 409 body's existingInvitationId).
// ============================================================

import React, { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import Modal from '../components/Modal';
import Field from '../components/Field';
import { issueInvitation, listInvitations, resendInvitation } from '../api/invitations';
import { listAdminTrialRequests } from '../api/adminTrialRequests';
import { clearClientManualInviteRequired } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { toast } from '../components/Toaster';
import './CreateTenantDialog.css';
import './ManualInviteBanner.css';

const ROLE_LABELS = {
  manager: 'Manager (full platform — recommended for the trial)',
  carer:   'Carer (CareNotes only)',
};

function isValidEmail(value) {
  return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function ManualInviteBanner({ clientId, manualInviteReason, onCleared }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ displayName: '', email: '', roleKey: 'manager' });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Set when the issue endpoint 409s on a duplicate pending invitation —
  // holds the conflicting email so the resend affordance can resolve it.
  const [duplicatePendingEmail, setDuplicatePendingEmail] = useState(null);
  const [resending, setResending] = useState(false);

  function openDialog() {
    setForm({ displayName: '', email: '', roleKey: 'manager' });
    setErrors({});
    setSubmitError(null);
    setDuplicatePendingEmail(null);
    setOpen(true);
    prefillFromTrialRequester();
  }

  // Best-effort prefill from the linked trial requester (Slice A):
  // the newest TrialRequests row carrying this ClientID. Never
  // clobbers text the operator has already typed; silent on failure.
  async function prefillFromTrialRequester() {
    try {
      const result = await listAdminTrialRequests({ clientId, pageSize: 1 });
      const trial = result && Array.isArray(result.items) ? result.items[0] : null;
      if (!trial) return;
      const requesterName = [trial.firstName, trial.lastName].filter(Boolean).join(' ').trim();
      setForm((prev) => ({
        ...prev,
        displayName: prev.displayName.trim() ? prev.displayName : requesterName,
        email: prev.email.trim() ? prev.email : (trial.email || ''),
      }));
    } catch (err) {
      // Prefill is a convenience — the operator can type the fields.
    }
  }

  function closeDialog() {
    if (submitting || resending) return;
    setOpen(false);
  }

  async function finishSuccess(message) {
    try {
      await clearClientManualInviteRequired(clientId);
    } catch (clearErr) {
      // Invitation went out; the flag clear is best-effort.
      toast.error('Invitation sent, but clearing the flag failed — refresh to retry.');
    }
    toast.success(message);
    setOpen(false);
    if (onCleared) onCleared();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const e = {};
    if (!form.displayName.trim()) e.displayName = 'Recipient name is required.';
    if (!isValidEmail(form.email)) e.email = 'A valid recipient email is required.';
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    setDuplicatePendingEmail(null);
    try {
      await issueInvitation({
        clientId,
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        roleKey: form.roleKey,
      });
      await finishSuccess('Invitation sent');
    } catch (err) {
      if (err && err.status === 409) {
        setSubmitError(
          'A pending invitation already exists for this email at this client. '
          + 'You can resend its email below (this refreshes the link and expiry).'
        );
        setDuplicatePendingEmail(form.email.trim().toLowerCase());
      } else {
        setSubmitError(translateError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  // One-click resend for the existing pending invitation the 409
  // pointed at. ApiError doesn't expose the 409 body, so the pending
  // row is resolved via listInvitations filtered to this client.
  async function handleResendExisting() {
    if (!duplicatePendingEmail) return;
    setResending(true);
    setSubmitError(null);
    try {
      const result = await listInvitations({ clientId, status: 'pending', limit: 200 });
      const match = (result.invitations || []).find(
        (inv) => (inv.email || '').toLowerCase() === duplicatePendingEmail
      );
      if (!match) {
        setSubmitError('Could not find the pending invitation — it may have just been redeemed or revoked. Try sending again.');
        setDuplicatePendingEmail(null);
        return;
      }
      await resendInvitation(match.invitationId);
      await finishSuccess('Invitation email resent');
    } catch (err) {
      setSubmitError(translateError(err));
    } finally {
      setResending(false);
    }
  }

  return (
    <>
      <div className="manual-invite-banner" role="alert">
        <AlertTriangle size={18} className="manual-invite-banner__icon" />
        <div className="manual-invite-banner__body">
          <strong className="manual-invite-banner__title">Invitation not sent</strong>
          <p className="manual-invite-banner__reason">
            The trial was provisioned but the requester’s invitation could not be sent.
            {manualInviteReason ? <> Reason: <span className="manual-invite-banner__code">{manualInviteReason}</span></> : null}
          </p>
        </div>
        <button type="button" className="manual-invite-banner__action" onClick={openDialog}>
          Send invitation
        </button>
      </div>

      <Modal isOpen={open} onClose={closeDialog} title="Send invitation" closeDisabled={submitting || resending}>
        <form className="create-tenant-form" onSubmit={handleSubmit} noValidate>
          {submitError && (
            <div className="create-tenant-form__banner" role="alert">{submitError}</div>
          )}
          {duplicatePendingEmail && (
            <div className="create-tenant-form__actions">
              <button
                type="button"
                className="create-tenant-form__submit"
                onClick={handleResendExisting}
                disabled={resending || submitting}
              >
                {resending && <Loader2 size={14} className="create-tenant-form__spinner" />}
                Resend the existing invitation
              </button>
            </div>
          )}
          <Field label="Recipient name" error={errors.displayName} required>
            <input
              type="text"
              value={form.displayName}
              maxLength={200}
              disabled={submitting}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              autoFocus
            />
          </Field>
          <Field label="Recipient email" error={errors.email} required>
            <input
              type="email"
              value={form.email}
              maxLength={200}
              disabled={submitting}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Role" required>
            <select
              value={form.roleKey}
              disabled={submitting}
              onChange={(e) => setForm({ ...form, roleKey: e.target.value })}
            >
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </Field>
          <div className="create-tenant-form__actions">
            <button type="button" className="create-tenant-form__cancel" onClick={closeDialog} disabled={submitting || resending}>
              Cancel
            </button>
            <button type="submit" className="create-tenant-form__submit" disabled={submitting || resending}>
              {submitting && <Loader2 size={14} className="create-tenant-form__spinner" />}
              Send invitation
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
